/**
 *
 * [Android rework 의 OutlookAvailabilityTime.java 의 내용을 포팅.]
 *
 * Oulook 에서 사용하던 time format (outlook 과 호환을 위해 사용)
 *
 * Example:
 * {
 *   "endDate": "2022-04-25T13:00:00+09:00",
 *   "endDateLabel": "오후 1:00",
 *   "startDate": "2022-04-25T12:30:00+09:00",
 *   "startDateLabel": "오후 12:30",
 *   "timeZone": "GMT+9"
 * }
 */

// import settings from 'popups/Settings'

import {
  localizedTimeString,
  localizedDateString_ddddMMMMDYYYY,
} from 'pages/calendar/utils/DateUtils'

// import dayjs from 'dayjs'

export interface OutlookAvailabilityTimeData {
  startDate: string
  startDateLabel: string
  endDate: string
  endDateLabel: string
  timeZone: string
}

export class OutlookAvailabilityTime implements OutlookAvailabilityTimeData {
  startDate: string
  startDateLabel: string
  endDate: string
  endDateLabel: string
  timeZone: string

  constructor(startCalendar: Date, endCalendar: Date) {
    // ex) "2022-04-25T13:00:00+09:00"
    this.startDate = OutlookAvailabilityTime.calendar2OutlookAvailabilityTime(startCalendar)

    // ex) 오후 7:00
    this.startDateLabel = OutlookAvailabilityTime.formatTime(startCalendar)

    // ex) "2022-04-25T13:00:00+09:00"
    this.endDate = OutlookAvailabilityTime.calendar2OutlookAvailabilityTime(endCalendar)

    // ex) 오후 7:00
    this.endDateLabel = OutlookAvailabilityTime.formatTime(endCalendar)

    // GMT offset calculation
    const timeZoneOffset = startCalendar.getTimezoneOffset() * -1 // Convert to positive offset
    const offset = timeZoneOffset / 60
    if (offset >= 0) {
      this.timeZone = `GMT+${offset}`
    } else {
      this.timeZone = `GMT${offset}`
    }
  }

  /**
   * Factory method to create OutlookAvailabilityTime instance
   */
  static createOutlookAvailabilityTime(
    startCalendar: Date,
    endCalendar: Date,
  ): OutlookAvailabilityTime {
    return new OutlookAvailabilityTime(startCalendar, endCalendar)
  }

  /**
   * Get day label in localized format
   * Uses settings-based formatting
   * ex) "Mon, Apr 25, 2022" or "2022년 4월 25일 월" (depending on locale)
   */
  getDayLabel(): string {
    const startDate = this.getStartDateCalendar()
    return localizedDateString_ddddMMMMDYYYY(startDate)
  }

  /**
   * Get start date as Date object
   */
  getStartDateCalendar(): Date {
    return OutlookAvailabilityTime.outlookAvailabilityTime2Calendar(this.startDate)
  }

  /**
   * Get end date as Date object
   */
  getEndDateCalendar(): Date {
    return OutlookAvailabilityTime.outlookAvailabilityTime2Calendar(this.endDate)
  }

  /**
   * Convert Outlook date string to Date object
   * "2022-04-25T19:00:00+09:00" -> Date
   */
  static outlookAvailabilityTime2Calendar(outlookDate: string): Date {
    return new Date(outlookDate)
  }

  /**
   * Convert Date object to Outlook date string
   * Date -> "2022-04-25T19:00:00+09:00"
   */
  static calendar2OutlookAvailabilityTime(calendar: Date): string {
    const year = calendar.getFullYear()
    const month = String(calendar.getMonth() + 1).padStart(2, '0')
    const day = String(calendar.getDate()).padStart(2, '0')
    const hours = String(calendar.getHours()).padStart(2, '0')
    const minutes = String(calendar.getMinutes()).padStart(2, '0')
    const seconds = String(calendar.getSeconds()).padStart(2, '0')

    const offset = OutlookAvailabilityTime.getOffset(calendar)

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`
  }

  /**
   * Get timezone offset in format "+09:00" or "-05:00"
   */
  static getOffset(calendar: Date): string {
    const timezoneOffset = calendar.getTimezoneOffset()
    const absOffset = Math.abs(timezoneOffset)
    const hours = Math.floor(absOffset / 60)
    const minutes = absOffset % 60

    const offsetStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

    if (timezoneOffset <= 0) {
      return `+${offsetStr}`
    } else {
      return `-${offsetStr}`
    }
  }

  /**
   * Format time in localized format
   * Uses settings-based formatting (respects user's 12h/24h preference)
   * ex) "7:00 PM" (12h) or "19:00" (24h)
   */
  static formatTime(date: Date): string {
    return localizedTimeString(date)
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): OutlookAvailabilityTimeData {
    return {
      startDate: this.startDate,
      startDateLabel: this.startDateLabel,
      endDate: this.endDate,
      endDateLabel: this.endDateLabel,
      timeZone: this.timeZone,
    }
  }

  /**
   * Create instance from plain object
   */
  static fromJSON(data: OutlookAvailabilityTimeData): OutlookAvailabilityTime {
    const instance = Object.create(OutlookAvailabilityTime.prototype)
    Object.assign(instance, data)
    return instance
  }

  toString(): string {
    return `startDate: ${this.startDate}\nstartDateLabel: ${this.startDateLabel}\nendDate: ${this.endDate}\nendDateLabel: ${this.endDateLabel}\ntimeZone: ${this.timeZone}`
  }
}
